import {useCallback} from 'react'
import CircleOcticon from '@github-ui/circle-octicon'
import {ShieldCheckIcon, ShieldIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {useQueryClient} from '@github-ui/react-query'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {useMergeBoxPageDataQueryKey} from '../../page-data/loaders/use-merge-box-page-data'
import type {ProofOfPresenceInitiationPayload} from '../../types'
import type {ProofOfPresenceRequirementPayload} from '../../page-data/payloads/merge-box'
import styles from './ProofOfPresenceSection.module.css'

const HEADER_ICON_SIZE = 32

const DOCS_URL = 'https://docs.github.com/authentication'

interface ProofOfPresenceSectionProps {
  requirement: ProofOfPresenceRequirementPayload
}

type ProofOfPresenceMetadata = ProofOfPresenceRequirementPayload['metadata']

const POP_TAB_NAME = 'pop-reauth'

function buildInitiationForm(initiation: ProofOfPresenceInitiationPayload): HTMLFormElement {
  const form = document.createElement('form')
  form.method = 'post'
  form.action = initiation.action
  form.hidden = true

  const fields: Record<string, string> = {
    authenticity_token: initiation.csrfToken ?? '',
    purpose: 'ref',
    pull_request_id: initiation.pullRequestId,
    return_to: initiation.returnTo,
  }

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.append(input)
  }

  return form
}

/**
 * Starts the proof of presence round trip in a new tab, resolving `true` once the tab
 * reports a recorded outcome and `false` on failure or dismissal.
 *
 * The form is appended to `document.body` because the merge box renders inside the pull
 * request page's own form, and the parser drops nested forms.
 *
 * The tab comes from the named `target`, while `window.open` provides a handle for
 * noticing a dismissed tab. If the browser blocks that tab, the form falls back to a
 * full-page submission.
 *
 * `display=popup` opts into the shared SSO terminal, which signals
 * `window.opener.external.ssoComplete` and closes — hence no `rel="noopener"`. The
 * signal is an invalidation hint, never proof: we always re-read from the server.
 */
function launchInitiationTab(initiation: ProofOfPresenceInitiationPayload): Promise<boolean> {
  const form = buildInitiationForm(initiation)
  const display = document.createElement('input')
  display.type = 'hidden'
  display.name = 'display'
  display.value = 'popup'
  form.append(display)

  const tab = window.open('', POP_TAB_NAME)

  document.body.append(form)

  if (!tab) {
    display.remove()
    form.removeAttribute('target')
    form.submit()
    return new Promise<boolean>(() => {})
  }

  form.target = POP_TAB_NAME
  form.submit()
  form.remove()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, compat/compat -- the bridge the shipped SSO terminal signals through.
  const external: any = window.external
  if (!external) return Promise.resolve(false)
  const previousHandler = external.ssoComplete

  return new Promise<boolean>(resolve => {
    let settled = false
    const finish = (result: boolean) => {
      if (settled) return
      settled = true
      external.ssoComplete = previousHandler ?? null
      if (closedPoll !== undefined) window.clearInterval(closedPoll)
      resolve(result)
    }

    external.ssoComplete = (data: {error?: unknown}) => finish(!data?.error)

    // A closed tab without a callback is a dismissal, so the caller isn't left hanging.
    const closedPoll = tab
      ? window.setInterval(() => {
          if (tab.closed) finish(false)
        }, 500)
      : undefined
  })
}

/**
 * The satisfied state: a green shield with a checkmark naming the user whose proof
 * satisfied the requirement. The Authenticate control is withdrawn.
 */
const VerifiedHeader = ({verifiedEmail}: {verifiedEmail: ProofOfPresenceMetadata['verifiedEmail']}) => (
  <MergeBoxSectionHeader
    title="Proof of presence verified"
    subtitle={verifiedEmail ? `This requirement was satisfied by ${verifiedEmail}` : undefined}
    icon={
      <CircleOcticon
        className={styles.satisfiedIcon}
        icon={() => <ShieldCheckIcon size={16} />}
        size={HEADER_ICON_SIZE}
      />
    }
  />
)

/**
 * The unsatisfied state: a neutral shield and the blocking message. The Authenticate
 * control is offered only to a viewer who can satisfy the requirement.
 */
const RequiredHeader = ({
  message,
  initiation,
}: {
  message: ProofOfPresenceRequirementPayload['message']
  initiation: ProofOfPresenceMetadata['initiation']
}) => {
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()

  const onAuthenticate = useCallback(async () => {
    if (!initiation) return
    if (!(await launchInitiationTab(initiation))) return
    await queryClient.refetchQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false})
  }, [initiation, queryClient, mergeBoxQueryKey])

  const authenticateButton = initiation ? (
    <button
      type="button"
      className="btn btn-sm"
      onClick={() => void onAuthenticate()}
      aria-label="Authenticate proof of presence, opens in a new tab"
    >
      Authenticate
    </button>
  ) : undefined

  return (
    <MergeBoxSectionHeader
      title="Proof of presence required"
      subtitle={
        <>
          {message ?? 'An authorized user must authenticate to merge.'}{' '}
          <Link href={DOCS_URL} target="_blank" rel="noopener noreferrer" inline>
            Learn more about proof of presence
          </Link>
        </>
      }
      icon={
        <CircleOcticon
          className={styles.unsatisfiedIcon}
          icon={() => <ShieldIcon size={16} />}
          size={HEADER_ICON_SIZE}
        />
      }
      rightSideContent={authenticateButton}
    />
  )
}

/**
 * Renders the enterprise proof of presence merge requirement.
 */
export const ProofOfPresenceSection = ({requirement}: ProofOfPresenceSectionProps) => {
  const {message, metadata, result} = requirement

  return (
    <section aria-label="Proof of presence" className="border-bottom color-border-subtle">
      {result === 'PASSED' ? (
        <VerifiedHeader verifiedEmail={metadata.verifiedEmail} />
      ) : (
        <RequiredHeader message={message} initiation={metadata.initiation} />
      )}
    </section>
  )
}
